import { useState } from "react";
import Dialog from "./Dialog";
import Icon from "./Icon";
import ProductForm from "./ProductForm";
import ImportPanel from "./ImportPanel";
import BulkPricePanel from "./BulkPricePanel";
import { UNDO_HOURS } from "./useBusiness";
import { FREE_PRODUCT_LIMIT, PRO_PRICE, PRO_TRIAL_DAYS } from "../../plans.js";
import { formatPrice } from "../../menu.js";

const ALL = "Todas";

// Lista de productos con búsqueda, filtro por categoría y el interruptor de Agotado (Pro).
const ProductsPanel = ({ data, creating, setCreating }) => {

    const { business, products, categories, canAddProduct } = data;
    const pro = business.subscribed === true;

    const [q, setQ] = useState("");
    const [cat, setCat] = useState(ALL);
    const [editing, setEditing] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [deletingCat, setDeletingCat] = useState(null);
    const [busy, setBusy] = useState(false);
    const [newCat, setNewCat] = useState("");
    const [addingCat, setAddingCat] = useState(false);
    const [importing, setImporting] = useState(false);
    const [upsell, setUpsell] = useState(null);
    const [pricing, setPricing] = useState(false);
    const [undoing, setUndoing] = useState(false);

    const term = q.trim().toLowerCase();
    const visible = products
        .filter((p) => (cat === ALL || p.category === cat) && (!term || p.name?.toLowerCase().includes(term)))
        .sort((a, b) => categories.indexOf(a.category) - categories.indexOf(b.category) || a.name.localeCompare(b.name, "es", { numeric: true }));

    const handleAddCategory = async (e) => {
        e.preventDefault();
        if (await data.addCategory(newCat)) {
            setCat(newCat.trim());
            setNewCat("");
            setAddingCat(false);
        }
    }

    const confirmDelete = async () => {
        setBusy(true);
        if (await data.deleteProduct(deleting)) setDeleting(null);
        setBusy(false);
    }

    const confirmDeleteCategory = async () => {
        setBusy(true);
        if (await data.deleteCategory(deletingCat)) {
            setDeletingCat(null);
            setCat(ALL);
        }
        setBusy(false);
    }

    const inCategory = (name) => products.filter((p) => p.category === name).length;

    if (importing && pro) {
        return <ImportPanel data={data} onClose={() => { setImporting(false); window.scrollTo(0, 0); }}></ImportPanel>
    }

    if (pricing && pro) {
        return <BulkPricePanel data={data} initialCategory={cat !== ALL ? cat : ""} onClose={() => { setPricing(false); window.scrollTo(0, 0); }}></BulkPricePanel>
    }

    const last = data.lastImport;
    const hoursLeft = last ? Math.max(1, Math.ceil(UNDO_HOURS - (Date.now() - last.at) / 3600000)) : 0;
    const undo = async () => {
        setUndoing(true);
        await data.undoImport();
        setUndoing(false);
    }

    const lastPrices = data.lastPriceChange;
    const pricesHoursLeft = lastPrices ? Math.max(1, Math.ceil(UNDO_HOURS - (Date.now() - lastPrices.at) / 3600000)) : 0;
    const undoPrices = async () => {
        setUndoing(true);
        await data.undoPriceChange();
        setUndoing(false);
    }

  return (
    <div className="lp-panel-page">
        <div className="lp-panel-head">
            <div>
                <h1>Productos <span className="lp-panel-count">{products.length}{business.subscribed ? "" : ` de ${FREE_PRODUCT_LIMIT}`}</span></h1>
                <p className="lp-panel-muted">Lo que cambies acá se ve al instante en tu menú.</p>
            </div>
            <div className="lp-panel-head-actions">
                <button type="button" className="lp-btn lp-btn-secondary" onClick={() => pro ? setPricing(true) : setUpsell("precios")} disabled={!products.length}>
                    <Icon name="money"></Icon> Cambiar precios {!pro && <span className="lp-panel-badge lp-imp-pro">Pro</span>}
                </button>
                <button type="button" className="lp-btn lp-btn-secondary" onClick={() => pro ? setImporting(true) : setUpsell("importar")}>
                    <Icon name="up"></Icon> Importar{pro ? " catálogo" : ""} {!pro && <span className="lp-panel-badge lp-imp-pro">Pro</span>}
                </button>
                <button type="button" className="lp-btn lp-btn-primary" onClick={() => setCreating(true)} disabled={!canAddProduct}>
                    <Icon name="plus"></Icon> Agregar producto
                </button>
            </div>
        </div>

        {pro && last &&
            <p className="lp-imp-note is-info">
                <Icon name="refresh" size={18} strokeWidth={2.2}></Icon>
                <span>
                    Importaste {last.count === 1 ? "1 producto" : `${last.count} productos`}. Si algo salió mal, podés deshacerlo durante {hoursLeft === 1 ? "1 hora más" : `${hoursLeft} horas más`}.{" "}
                    <button type="button" className="lp-text-link" disabled={undoing} onClick={undo}>{undoing ? "Deshaciendo..." : "Deshacer importación"}</button>{" · "}
                    <button type="button" className="lp-text-link" onClick={data.dismissLastImport}>Está todo bien</button>
                </span>
            </p>
        }

        {pro && lastPrices &&
            <p className="lp-imp-note is-info">
                <Icon name="refresh" size={18} strokeWidth={2.2}></Icon>
                <span>
                    Cambiaste {lastPrices.count === 1 ? "1 precio" : `${lastPrices.count} precios`}{lastPrices.description ? ` (${lastPrices.description})` : ""}. Si algo salió mal, podés deshacerlo durante {pricesHoursLeft === 1 ? "1 hora más" : `${pricesHoursLeft} horas más`}.{" "}
                    <button type="button" className="lp-text-link" disabled={undoing} onClick={undoPrices}>{undoing ? "Deshaciendo..." : "Deshacer cambio de precios"}</button>{" · "}
                    <button type="button" className="lp-text-link" onClick={data.dismissLastPriceChange}>Está todo bien</button>
                </span>
            </p>
        }

        {!pro && products.length > 0 &&
            <p className="lp-panel-notice">Con Pro podés marcar productos como agotados, ponerles etiquetas como Nuevo o Sin TACC y cambiar todos los precios de una. <a href="#cuenta" className="lp-text-link">Ver Pro</a></p>
        }

        {!canAddProduct &&
            <p className="lp-panel-notice">Llegaste a los {FREE_PRODUCT_LIMIT} productos del plan Gratis. Con Pro cargás todos los que quieras.</p>
        }

        <section className="lp-panel-card">
            <div className="lp-panel-search">
                <label htmlFor="buscar-producto" className="lp-panel-sr">Buscar producto</label>
                <Icon name="search" size={18}></Icon>
                <input id="buscar-producto" type="search" placeholder="Buscar producto…" value={q} onChange={(e) => setQ(e.target.value)}/>
            </div>

            <div className="lp-panel-chips" role="group" aria-label="Filtrar por categoría">
                {[ALL, ...categories].map((c) => (
                    <button key={c} type="button" className={cat === c ? "lp-panel-chip lp-panel-chip-on" : "lp-panel-chip"} aria-pressed={cat === c} onClick={() => setCat(c)}>
                        {c}
                    </button>
                ))}
                {addingCat ?
                    <form className="lp-panel-chip-form" onSubmit={handleAddCategory}>
                        <label htmlFor="nueva-categoria" className="lp-panel-sr">Nombre de la categoría</label>
                        <input id="nueva-categoria" type="text" autoFocus required maxLength={40} placeholder="Nueva categoría" value={newCat} onChange={(e) => setNewCat(e.target.value)}/>
                        <button type="submit" className="lp-panel-chip lp-panel-chip-on">Crear</button>
                        <button type="button" className="lp-panel-chip" onClick={() => { setAddingCat(false); setNewCat(""); }}>Cancelar</button>
                    </form> :
                    <button type="button" className="lp-panel-chip lp-panel-chip-add" onClick={() => setAddingCat(true)}><Icon name="plus" size={16} strokeWidth={2.6}></Icon> Agregar categoría</button>
                }
            </div>

            {cat !== ALL &&
                <div className="lp-panel-cat-tools">
                    <span>{inCategory(cat)} {inCategory(cat) === 1 ? "producto" : "productos"} en {cat}</span>
                    <button type="button" className="lp-panel-link-danger" onClick={() => setDeletingCat(cat)}>Eliminar categoría</button>
                </div>
            }

            {visible.length ?
                <ul className="lp-panel-products">
                    {visible.map((p) => (
                        <li key={p.id} className={p.agotado ? "lp-panel-product lp-panel-product-off" : "lp-panel-product"}>
                            {p.image ? <img src={p.image} alt="" className="lp-panel-thumb"/> : <span className="lp-panel-thumb" aria-hidden="true"></span>}
                            <div className="lp-panel-product-info">
                                <span className="lp-panel-product-name">{p.name}</span>
                                <span className="lp-panel-muted">{p.category} · {formatPrice(p.price)}{pro && p.etiquetas?.length ? " · " + p.etiquetas.join(", ") : ""}</span>
                            </div>
                            {/* Marcar agotado es de Pro; en Gratis solo se puede volver a Disponible. */}
                            <button type="button" className="lp-panel-switch" role="switch" aria-checked={!!p.agotado} aria-label={`${p.name} agotado`} disabled={!pro && !p.agotado} title={!pro && !p.agotado ? "Marcar agotados es del plan Pro" : undefined} onClick={() => data.toggleAgotado(p)}>
                                <span className="lp-panel-switch-track"><span className="lp-panel-switch-knob"></span></span>
                                <span className="lp-panel-switch-text">{p.agotado ? "Agotado" : "Disponible"}</span>
                            </button>
                            <div className="lp-panel-row-actions">
                                <button type="button" className="lp-panel-icon-btn" aria-label={`Editar ${p.name}`} onClick={() => setEditing(p)}><Icon name="edit" size={18}></Icon></button>
                                <button type="button" className="lp-panel-icon-btn lp-panel-icon-danger" aria-label={`Eliminar ${p.name}`} onClick={() => setDeleting(p)}><Icon name="trash" size={18}></Icon></button>
                            </div>
                        </li>
                    ))}
                </ul> :
                <p className="lp-panel-empty">
                    {products.length ? "No hay productos que coincidan." : categories.length ? "Todavía no cargaste productos. Empezá con el botón Agregar producto." : "Creá tu primera categoría con Agregar categoría y después cargá tus productos."}
                </p>
            }
        </section>

        <Dialog open={creating} title="Agregar producto" onClose={() => setCreating(false)}>
            <ProductForm pro={business.subscribed === true} categories={categories} defaultCategory={cat !== ALL ? cat : ""} onCancel={() => setCreating(false)} onSubmit={async (fields, image) => {
                const ok = await data.createProduct(fields, image);
                if (ok) setCreating(false);
                return ok;
            }}></ProductForm>
        </Dialog>

        <Dialog open={!!editing} title="Editar producto" onClose={() => setEditing(null)}>
            {editing &&
                <ProductForm key={editing.id} pro={business.subscribed === true} product={editing} categories={categories} onCancel={() => setEditing(null)} onSubmit={async (fields, image) => {
                    const ok = await data.updateProduct(editing, fields, image);
                    if (ok) setEditing(null);
                    return ok;
                }}></ProductForm>
            }
        </Dialog>

        <Dialog open={upsell === "precios"} title="Cambiar precios de una" onClose={() => setUpsell(null)}>
            <div className="lp-panel-stack lp-imp-gap">
                <p className="lp-panel-muted">Con el plan Pro actualizás los precios de todo tu menú en segundos, sin editar producto por producto.</p>
                <ul className="lp-imp-feat">
                    <li><span className="lp-imp-feat-ic" aria-hidden="true"><Icon name="money" size={18}></Icon></span><span><strong>Por porcentaje o monto fijo</strong><span className="lp-panel-muted">Subí un 10% todo, o sumale $500 a las pizzas.</span></span></li>
                    <li><span className="lp-imp-feat-ic" aria-hidden="true"><Icon name="grid" size={18}></Icon></span><span><strong>A todo o a una parte</strong><span className="lp-panel-muted">Todo el catálogo, una categoría o los productos que elijas.</span></span></li>
                    <li><span className="lp-imp-feat-ic" aria-hidden="true"><Icon name="check" size={18} strokeWidth={2.4}></Icon></span><span><strong>Precios redondos</strong><span className="lp-panel-muted">Redondeamos a $10, $50 o $100 para que no queden precios raros.</span></span></li>
                    <li><span className="lp-imp-feat-ic" aria-hidden="true"><Icon name="refresh" size={18}></Icon></span><span><strong>Ves cómo queda y podés deshacer</strong><span className="lp-panel-muted">Antes de guardar te mostramos cada precio, y tenés 24 horas para volver atrás.</span></span></li>
                </ul>
                <a className="lp-btn lp-btn-primary lp-imp-cta" href="#cuenta" onClick={() => setUpsell(null)}>Probar Pro gratis {PRO_TRIAL_DAYS} días</a>
                <p className="lp-panel-muted lp-imp-cta-note">Después {PRO_PRICE} por mes. Cancelás cuando quieras.</p>
            </div>
        </Dialog>

        <Dialog open={upsell === "importar"} title="Importar tu catálogo" onClose={() => setUpsell(null)}>
            <div className="lp-panel-stack lp-imp-gap">
                <p className="lp-panel-muted">Con el plan Pro pasás tu carta entera a MiQRapp en un par de minutos, sin cargar producto por producto.</p>
                <ul className="lp-imp-feat">
                    <li><span className="lp-imp-feat-ic" aria-hidden="true"><Icon name="grid" size={18}></Icon></span><span><strong>Desde Excel o CSV</strong><span className="lp-panel-muted">Con nuestra plantilla o con tu propia planilla.</span></span></li>
                    <li><span className="lp-imp-feat-ic" aria-hidden="true"><Icon name="check" size={18} strokeWidth={2.4}></Icon></span><span><strong>Revisás antes de publicar</strong><span className="lp-panel-muted">Te marcamos lo que falta o está mal para corregirlo ahí mismo.</span></span></li>
                    <li><span className="lp-imp-feat-ic" aria-hidden="true"><Icon name="box" size={18}></Icon></span><span><strong>Sin límite de productos</strong><span className="lp-panel-muted">En Gratis llegás hasta {FREE_PRODUCT_LIMIT}; con Pro cargás todos los que quieras.</span></span></li>
                    <li><span className="lp-imp-feat-ic" aria-hidden="true"><Icon name="star" size={18}></Icon></span><span><strong>Desde un PDF o fotos de tu carta</strong><span className="lp-panel-muted">La inteligencia artificial lee nombres, precios y categorías por vos.</span></span></li>
                </ul>
                <a className="lp-btn lp-btn-primary lp-imp-cta" href="#cuenta" onClick={() => setUpsell(null)}>Probar Pro gratis {PRO_TRIAL_DAYS} días</a>
                <p className="lp-panel-muted lp-imp-cta-note">Después {PRO_PRICE} por mes. Cancelás cuando quieras.</p>
            </div>
        </Dialog>

        <Dialog open={!!deleting} title="Eliminar producto" onClose={() => setDeleting(null)}>
            <p className="lp-panel-muted">¿Seguro que querés eliminar <strong>{deleting?.name}</strong>? No se puede deshacer.</p>
            <div className="lp-panel-actions">
                <button type="button" className="lp-btn lp-panel-btn-danger" disabled={busy} onClick={confirmDelete}>{busy ? "Eliminando..." : "Eliminar"}</button>
                <button type="button" className="lp-btn lp-btn-secondary" onClick={() => setDeleting(null)}>Cancelar</button>
            </div>
        </Dialog>

        <Dialog open={!!deletingCat} title="Eliminar categoría" onClose={() => setDeletingCat(null)}>
            <p className="lp-panel-muted">
                Se va a eliminar <strong>{deletingCat}</strong>
                {deletingCat && inCategory(deletingCat) ? (inCategory(deletingCat) === 1 ? " junto con su producto" : ` junto con sus ${inCategory(deletingCat)} productos`) : ""}. No se puede deshacer.
            </p>
            <div className="lp-panel-actions">
                <button type="button" className="lp-btn lp-panel-btn-danger" disabled={busy} onClick={confirmDeleteCategory}>{busy ? "Eliminando..." : "Eliminar"}</button>
                <button type="button" className="lp-btn lp-btn-secondary" onClick={() => setDeletingCat(null)}>Cancelar</button>
            </div>
        </Dialog>
    </div>
  )
}

export default ProductsPanel
